import type { TextFieldProps } from '@rikstv/shared-components/dist/components/textfield/TextField';
import { format } from 'date-fns/format';
import { composeValidators, validators } from '~/utils/validation';
import type { BasketFields } from './useBasket';

type VerificationResponse = NonNullable<BasketFields['verificationResponse']>;
export type CustomerInfoFields = Exclude<keyof VerificationResponse['formValues'], 'houseNoAlpha' | 'houseNumber'>;

export function getFormFieldProps({
  birthDate,
  city,
  email,
  firstName,
  lastName,
  mobilePhone,
  street,
  zip,
}: VerificationResponse['formValues']): Record<CustomerInfoFields, TextFieldProps> {
  const validMobilePhone = Boolean(mobilePhone);
  return {
    email: {
      defaultValue: email,
      type: 'email',
      name: 'email',
      label: 'E-post',
      required: true,
      autoComplete: 'email',
      onInput: createValidateFn(fieldValidators.email),
    },
    mobilePhone: {
      defaultValue: mobilePhone,
      readOnly: validMobilePhone,
      type: 'tel',
      name: 'mobilePhone',
      label: 'Mobilnummer',
      required: true,
      autoComplete: 'tel',
      onInput: validMobilePhone ? undefined : createValidateFn(fieldValidators.mobile),
    },
    firstName: {
      defaultValue: firstName,
      readOnly: firstName != null,
      type: 'text',
      name: 'firstName',
      label: 'Fornavn',
      required: true,
      autoComplete: 'given-name',
      onInput: firstName != null ? undefined : createValidateFn(fieldValidators.firstname),
    },
    lastName: {
      defaultValue: lastName,
      readOnly: lastName != null,
      type: 'text',
      name: 'lastName',
      label: 'Etternavn',
      required: true,
      autoComplete: 'family-name',
      onInput: lastName != null ? undefined : createValidateFn(fieldValidators.lastname),
    },
    birthDate: {
      type: 'date',
      readOnly: birthDate != null,
      name: 'birthDate',
      label: 'Fødselsdato',
      defaultValue: birthDate ? format(birthDate, 'yyyy-MM-dd') : '',
      required: true,
      autoComplete: 'bday',
      onInput: birthDate != null ? undefined : createValidateFn(fieldValidators.birthdate),
    },
    street: {
      defaultValue: street,
      type: 'text',
      name: 'street',
      label: 'Adresse',
      required: true,
      autoComplete: 'street-address',
      onInput: createValidateFn(fieldValidators.adress),
    },
    city: {
      defaultValue: city,
      type: 'text',
      name: 'city',
      label: 'Poststed',
      required: true,
      autoComplete: 'address-level2',
      onInput: createValidateFn(fieldValidators.city),
    },
    zip: {
      defaultValue: zip,
      type: 'text',
      name: 'zip',
      label: 'Postnummer',
      required: true,
      autoComplete: 'postal-code',
      onInput: createValidateFn(fieldValidators.zip),
    },
  };
}

// use browser native validation UI but override with custom messages
const createValidateFn =
  (validateFn: (value: string) => string | undefined) =>
  ({ target }: React.FormEvent<HTMLInputElement>) => {
    if (target instanceof HTMLInputElement) target.setCustomValidity(validateFn(target.value) || '');
  };

const fieldValidators = {
  email: composeValidators(validators.required('E-postadressen må fylles ut'), validators.email),
  mobile: composeValidators(validators.required('Mobilnummer må fylles ut'), validators.phone),
  firstname: composeValidators(validators.required('Fornavn må fylles ut'), validators.text('Ugyldig fornavn')),
  lastname: composeValidators(validators.required('Etternavn må fylles ut'), validators.text('Ugyldig etternavn')),
  birthdate: composeValidators(validators.required('Fødselsdato må fylles ut'), validators.minAge(18)),
  adress: composeValidators(validators.required('Adresse må fylles ut'), validators.invalidComma),
  zip: composeValidators(validators.required('Postnummer må fylles ut'), validators.zip),
  city: composeValidators(validators.required('Poststed må fylles ut'), validators.text('Ugyldig poststed')),
};
